import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { OnboardingStep } from "./onboarding-types";
import { OnboardingTooltip } from "./onboarding-tooltip";

interface OnboardingContextType {
  isActive: boolean;
  currentStep: number;
  steps: OnboardingStep[];
  startOnboarding: (steps: OnboardingStep[], startFromStep?: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipToEnd: () => void;
  closeOnboarding: () => void;
  setSteps: (steps: OnboardingStep[]) => void;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

interface OnboardingProviderProps {
  children: ReactNode;
  autoStart?: boolean;
  persistProgress?: boolean;
  storageKey?: string;
}

export function OnboardingProvider({ 
  children, 
  autoStart = false, 
  persistProgress = true,
  storageKey = 'onboarding_progress'
}: OnboardingProviderProps) {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);

  // Load saved progress
  useEffect(() => {
    if (persistProgress) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const { completed, currentStep: savedStep } = JSON.parse(saved);
          if (completed) {
            setIsActive(false);
          } else if (savedStep !== undefined) {
            setCurrentStep(savedStep);
          }
        } catch (error) {
          console.warn('Failed to parse onboarding progress:', error);
        }
      }
    }
  }, [persistProgress, storageKey]);

  // Save progress
  const saveProgress = (step: number, completed = false) => {
    if (persistProgress) {
      localStorage.setItem(storageKey, JSON.stringify({
        currentStep: step,
        completed,
        timestamp: Date.now()
      }));
    }
  };

  const startOnboarding = (newSteps: OnboardingStep[], startFromStep = 0) => {
    setSteps(newSteps);
    setCurrentStep(startFromStep);
    setIsActive(true);
    saveProgress(startFromStep);
  };

  const nextStep = () => {
    const nextStepIndex = currentStep + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(nextStepIndex);
      saveProgress(nextStepIndex);
    } else {
      closeOnboarding();
    }
  };

  const previousStep = () => {
    const prevStepIndex = Math.max(0, currentStep - 1);
    setCurrentStep(prevStepIndex);
    saveProgress(prevStepIndex);
  };

  const skipToEnd = () => {
    closeOnboarding();
  };

  const closeOnboarding = () => {
    setIsActive(false);
    saveProgress(currentStep, true);
  };

  return (
    <OnboardingContext.Provider
      value={{
        isActive,
        currentStep,
        steps,
        startOnboarding,
        nextStep,
        previousStep,
        skipToEnd,
        closeOnboarding,
        setSteps,
      }}
    >
      {children}
      
      {/* Render tooltip if active */}
      {isActive && steps.length > 0 && (
        <OnboardingTooltip
          steps={steps}
          isActive={isActive}
          currentStep={currentStep}
          onNext={nextStep}
          onPrevious={previousStep}
          onSkip={skipToEnd}
          onClose={closeOnboarding}
        />
      )}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}

// Hook for triggering onboarding on specific pages
export function usePageOnboarding(pageId: string, steps: OnboardingStep[], autoStart = true) {
  const { startOnboarding, isActive } = useOnboarding();
  
  useEffect(() => {
    if (autoStart && !isActive) {
      // Check if this page's onboarding was already completed
      const completed = localStorage.getItem(`onboarding_${pageId}_completed`);
      if (!completed) {
        // Small delay to ensure DOM elements are rendered
        const timer = setTimeout(() => {
          startOnboarding(steps);
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }
  }, [pageId, steps, autoStart, startOnboarding, isActive]);

  const markPageOnboardingComplete = () => {
    localStorage.setItem(`onboarding_${pageId}_completed`, 'true');
  };

  const resetPageOnboarding = () => {
    localStorage.removeItem(`onboarding_${pageId}_completed`);
  };

  return { markPageOnboardingComplete, resetPageOnboarding };
}